import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { Moon, Palette, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cls';

import { PRESETS, type ThemePresetId } from './presets';
import {
  applyCustomRamp,
  applyPreset,
  getMode,
  getPreset,
  rampFromHex,
  setMode,
  type ThemeMode,
} from './themeRuntime';

/** Floating dropdown that lets the user pick one of 8 brand presets,
 *  enter a custom HEX, and toggle dark/light mode. Persists across
 *  reloads via `themeRuntime`. */
export function ThemeSwitcher() {
  const [mode, setModeState] = useState<ThemeMode>(getMode());
  const [active, setActive] = useState<ThemePresetId>(getPreset());
  const [hex, setHex] = useState('');

  // Keep state in sync if some other component changes the theme.
  useEffect(() => {
    setModeState(getMode());
    setActive(getPreset());
  }, []);

  const onPick = (id: ThemePresetId) => {
    applyPreset(id);
    setActive(id);
  };

  const onCustom = () => {
    const ramp = rampFromHex(hex);
    if (!ramp) return;
    applyCustomRamp(ramp);
    setActive('custom' as ThemePresetId); // visual marker only
  };

  const toggleMode = () => {
    const next: ThemeMode = mode === 'light' ? 'dark' : 'light';
    setMode(next);
    setModeState(next);
  };

  return (
    <div className="flex items-center gap-1.5">
      <Button
        variant="ghost"
        size="sm"
        onClick={toggleMode}
        aria-label="Toggle dark mode"
        className="!h-9 !w-9 !p-0 rounded-full"
      >
        {mode === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
      </Button>

      <Menu as="div" className="relative">
        <MenuButton
          as={Button}
          variant="ghost"
          size="sm"
          aria-label="Choose theme"
          className="!h-9 !w-9 !p-0 rounded-full"
        >
          <Palette className="h-4 w-4" />
        </MenuButton>
        <MenuItems
          className={cn(
            'absolute right-0 z-50 mt-2 w-72 origin-top-right',
            'card p-3 space-y-3 animate-slide-up',
          )}
        >
          <div>
            <div className="stat-label mb-2">Brand colour</div>
            <div className="grid grid-cols-4 gap-2">
              {PRESETS.map((p) => {
                const swatch = `rgb(${p.ramp[500]})`;
                const isActive = p.id === active;
                return (
                  <MenuItem key={p.id}>
                    <button
                      type="button"
                      onClick={() => onPick(p.id)}
                      title={p.label}
                      className={cn(
                        'h-10 rounded-xl flex items-center justify-center',
                        'border-2 transition-colors',
                        isActive ? 'border-ink' : 'border-line hover:border-ink-soft',
                      )}
                      style={{ backgroundColor: swatch }}
                    >
                      <span className="sr-only">{p.label}</span>
                    </button>
                  </MenuItem>
                );
              })}
            </div>
          </div>

          <div className="pt-2 border-t border-line">
            <div className="stat-label mb-2">Custom HEX</div>
            <div className="flex gap-2">
              <input
                type="text"
                value={hex}
                onChange={(e) => setHex(e.target.value)}
                placeholder="#10B981"
                className="flex-1 h-9 px-3 rounded-lg border border-line bg-surface text-sm"
              />
              <Button size="sm" onClick={onCustom} disabled={!rampFromHex(hex)}>
                Apply
              </Button>
            </div>
          </div>
        </MenuItems>
      </Menu>
    </div>
  );
}
